import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { isEmpty, reject } from "lodash/fp";
import { useParams } from "react-router";

import { anyAreEdited } from "@app/analytics/MeetingReport/utils";
import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import useSingleSessionInfoQuery from "@app/common/hooks/useSingleSessionInfoQuery";
import type { SessionNote } from "@app/common/types";
import { populateTimestamps } from "@app/common/utils/notesUtils";

import useVideoStore from "../stores/video";

const EMPTY_NOTES: SessionNote[] = [];

const useMeetingReportSessionNotes = (
  opts: { enabled: boolean } = { enabled: true }
) => {
  const { user } = useAuth();
  const { sessionId } = useParams();

  const queryKey = ["notes", sessionId];

  const notesQuery = useQuery({
    queryKey,
    queryFn: () => apiClient.session.notes.getNotes(sessionId!),
    enabled: Boolean(sessionId) && Boolean(user) && opts.enabled,
    refetchOnWindowFocus: false,
  });
  const { data: sessionInfo, isLoading: isSessionInfoLoading } =
    useSingleSessionInfoQuery(sessionId, opts);

  const videoStartTimeOffset = useVideoStore(
    (state) => state.videoStartTimeOffset
  );

  const notes = notesQuery.data ?? EMPTY_NOTES;

  const populatedNotes = useMemo(() => {
    if (isEmpty(notes) || !sessionInfo?.startTime) return [];

    const undeletedNotes = reject({ isDeleted: true }, notes);
    return populateTimestamps(
      undeletedNotes,
      sessionInfo.startTime,
      videoStartTimeOffset
    );
  }, [notes, sessionInfo?.startTime]);

  return {
    notes: populatedNotes,
    isNotesEdited:
      // deleted notes are returned from the API but are filtered out in the
      // memo above
      notes.length > populatedNotes.length || anyAreEdited(populatedNotes),
    isInitialLoading: isSessionInfoLoading || notesQuery.isLoading,
  };
};

export default useMeetingReportSessionNotes;
